import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { AfterimagePass } from "three/examples/jsm/postprocessing/AfterimagePass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

export interface EngineHooks {
  onFrame: (dt: number, elapsed: number) => void;
  /** Fires when the rolling frame-time avg exceeds the budget for the
   *  configured window — opportunity to downgrade quality. */
  onPerfPressure?: (avgMs: number) => void;
}

// God-ray pass — radial light streaks from screen center. Cheap-ish: 12 samples
// along the radial vector from each pixel toward the center, accumulating
// bright contributions only (luminance threshold). Mixed back into the source
// proportional to boost. Gated by motionEffectsEnabled (off on iOS / low).
const GodRaysShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    amount: { value: 0.0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;
    void main() {
      vec4 src = texture2D(tDiffuse, vUv);
      if (amount < 0.001) { gl_FragColor = src; return; }
      vec2 dir = vec2(0.5) - vUv;
      // Step length scales with radial distance so center reads cleanest.
      float r = length(dir);
      vec2 step = dir / 12.0 * amount;
      vec3 acc = vec3(0.0);
      vec2 p = vUv;
      for (int i = 0; i < 12; i++) {
        p += step;
        vec3 c = texture2D(tDiffuse, p).rgb;
        // Bright-pass threshold so only emissive content streaks.
        float lum = dot(c, vec3(0.299, 0.587, 0.114));
        acc += c * smoothstep(0.5, 0.9, lum);
      }
      acc /= 12.0;
      // Falloff: outer rim contributes most, center untouched.
      gl_FragColor = vec4(src.rgb + acc * (0.8 + r * 1.5), src.a);
    }
  `,
};

// Custom radial chromatic-aberration shader. Cheap (3 samples, screen-space).
// Amount is scaled by r² so the shift falls off to ~zero at screen center.
const ChromaticAberrationShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    amount: { value: 0.0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;
    void main() {
      vec2 c = vUv - 0.5;
      float r2 = dot(c, c);
      vec2 dir = c * amount * r2 * 6.0;
      float r = texture2D(tDiffuse, vUv + dir).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - dir).b;
      gl_FragColor = vec4(r, g, b, 1.0);
    }
  `,
};

export class Engine {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly composer: EffectComposer;
  readonly clock = new THREE.Clock();
  private bloomPass!: UnrealBloomPass;
  private afterimagePass!: AfterimagePass;
  private godRaysPass!: ShaderPass;
  private chromaticPass!: ShaderPass;

  private baseFov = 70;
  private peakFov = 78;
  private motionEffectsEnabled = true;       // false on low quality / coarse pointer / reduced-motion
  private boostLevel = 0;

  private rafId = 0;
  private hooks: EngineHooks;
  private running = false;
  private resizeObserver?: ResizeObserver;

  // Frame-time sampler
  private frameTimes = new Float32Array(120); // ring buffer, last ~2s at 60fps
  private frameIdx = 0;
  private frameCount = 0;
  private lastTickMs = 0;
  private perfBudgetMs = 22; // ≈45 fps; below this triggers pressure
  private perfPressureCooldownMs = 0;

  constructor(canvas: HTMLCanvasElement, hooks: EngineHooks) {
    this.hooks = hooks;

    // MSAA is *very* expensive on iOS Safari WebGL — drop it on coarse pointers
    // (touch devices). The bloom pass + thin emissive ring edges look fine at
    // 1.5× DPR without MSAA; the perceived smoothness gain isn't worth the
    // 2-4× fragment-throughput tax on Apple GPUs.
    const isCoarse = typeof matchMedia !== "undefined" &&
      matchMedia("(pointer: coarse)").matches;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !isCoarse,
      powerPreference: "high-performance",
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.setClearColor(0x05060a, 1);

    this.camera = new THREE.PerspectiveCamera(this.baseFov, 1, 0.1, 800);
    this.camera.position.set(0, 1.4, 6);

    this.scene.fog = new THREE.FogExp2(0x05060a, 0.012);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.7, 0.6, 0.85);
    this.composer.addPass(this.bloomPass);
    // Afterimage = frame-accumulator trail. Cheap motion-blur surrogate.
    this.afterimagePass = new AfterimagePass(0.85);
    this.afterimagePass.enabled = false;
    this.composer.addPass(this.afterimagePass);
    // God-rays — radial bright-pass streaks. Driven by boost like the others.
    this.godRaysPass = new ShaderPass(GodRaysShader);
    this.godRaysPass.enabled = false;
    this.composer.addPass(this.godRaysPass);
    // Chromatic aberration runs last so it tints the bloomed+trailed result.
    this.chromaticPass = new ShaderPass(ChromaticAberrationShader);
    this.chromaticPass.enabled = false;
    this.composer.addPass(this.chromaticPass);

    // Coarse pointers (mobile) get motion effects disabled by default. The
    // quality profile may flip this on for medium/high non-coarse desktop.
    if (isCoarse) this.motionEffectsEnabled = false;

    const ambient = new THREE.AmbientLight(0x88aaff, 0.4);
    const key = new THREE.DirectionalLight(0xffffff, 0.6);
    key.position.set(4, 6, 3);
    const rim = new THREE.DirectionalLight(0x00e0ff, 0.5);
    rim.position.set(-5, -2, -4);
    this.scene.add(ambient, key, rim);

    this.setupResize(canvas);
  }

  private setupResize(canvas: HTMLCanvasElement) {
    const apply = () => {
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      this.renderer.setSize(w, h, false);
      this.composer.setSize(w, h);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    };
    apply();
    this.resizeObserver = new ResizeObserver(apply);
    this.resizeObserver.observe(canvas);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    this.lastTickMs = performance.now();
    const loop = () => {
      if (!this.running) return;
      const dt = Math.min(this.clock.getDelta(), 0.05);
      const t = this.clock.elapsedTime;
      this.hooks.onFrame(dt, t);
      this.composer.render();

      // Sample frame time from wall clock (clock.getDelta is the same window,
      // but performance.now gives a stable ms reading independent of clamp).
      const now = performance.now();
      const frameMs = now - this.lastTickMs;
      this.lastTickMs = now;
      this.frameTimes[this.frameIdx] = frameMs;
      this.frameIdx = (this.frameIdx + 1) % this.frameTimes.length;
      if (this.frameCount < this.frameTimes.length) this.frameCount++;
      this.perfPressureCooldownMs = Math.max(0, this.perfPressureCooldownMs - frameMs);

      // Once we have a full window AND we're past startup warmup, check pressure.
      if (
        this.frameCount === this.frameTimes.length &&
        this.perfPressureCooldownMs === 0 &&
        this.hooks.onPerfPressure
      ) {
        let sum = 0;
        for (let i = 0; i < this.frameTimes.length; i++) sum += this.frameTimes[i];
        const avg = sum / this.frameTimes.length;
        if (avg > this.perfBudgetMs) {
          this.hooks.onPerfPressure(avg);
          // Hold off for 5 seconds so we don't downgrade twice in a row.
          this.perfPressureCooldownMs = 5000;
        }
      }

      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  /** Last-window average frame time in ms (0 if not enough samples yet). */
  get avgFrameMs(): number {
    if (this.frameCount === 0) return 0;
    let sum = 0;
    const n = this.frameCount;
    for (let i = 0; i < n; i++) sum += this.frameTimes[i];
    return sum / n;
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  setPixelRatio(cap: number) {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));
  }

  setBloom(strength: number) {
    this.bloomPass.strength = strength;
    this.bloomPass.enabled = strength > 0;
  }

  /**
   * Master switch for cinematic effects that motion-sensitive users should
   * be able to disable: motion blur, FOV pulse, chromatic aberration.
   * Called by main.ts whenever the quality profile or reduced-motion changes.
   */
  setMotionEffectsEnabled(enabled: boolean) {
    this.motionEffectsEnabled = enabled;
    if (!enabled) {
      this.afterimagePass.enabled = false;
      this.chromaticPass.enabled = false;
      this.godRaysPass.enabled = false;
      if (this.camera.fov !== this.baseFov) {
        this.camera.fov = this.baseFov;
        this.camera.updateProjectionMatrix();
      }
    }
  }

  /**
   * Per-frame boost feed (0..1). Drives FOV pulse, motion-blur strength,
   * and chromatic aberration amount. Silent when motion effects are off.
   */
  setBoost(boost: number, dt: number) {
    const b = Math.max(0, Math.min(1, boost));
    this.boostLevel = b;
    if (!this.motionEffectsEnabled) return;

    // FOV pulse: damp toward baseFov + boost*(peak-base)
    const targetFov = this.baseFov + (this.peakFov - this.baseFov) * b;
    const newFov = THREE.MathUtils.damp(this.camera.fov, targetFov, 4, dt);
    if (Math.abs(newFov - this.camera.fov) > 0.005) {
      this.camera.fov = newFov;
      this.camera.updateProjectionMatrix();
    }

    // Motion blur (afterimage trail): 0 at idle → 0.85 at full boost. The
    // pure-multiplicative curve removes the abrupt pop the old `0.5 + …`
    // baseline produced when the pass first enabled at boost > 0.03.
    const targetDamp = b * 0.85;
    const dampU = (this.afterimagePass.uniforms as any).damp;
    if (dampU) {
      dampU.value = THREE.MathUtils.lerp(dampU.value, targetDamp, 1 - Math.pow(0.001, dt));
    }
    this.afterimagePass.enabled = b > 0.03;

    // Chromatic aberration on the edges — proportional to boost. Bumped from
    // 0.006 because the radial r² falloff in the shader was making the effect
    // imperceptible at peak; 0.012 reads as a clear edge tint at full boost
    // without bleeding into the central play area.
    (this.chromaticPass.uniforms as any).amount.value = b * 0.012;
    this.chromaticPass.enabled = b > 0.05;

    // God-rays — radial bright-pass streaks. Amount holds steady around 0.6
    // when active (the sample step scales internally), gated by boost.
    (this.godRaysPass.uniforms as any).amount.value = b * 0.6;
    this.godRaysPass.enabled = b > 0.1;
  }

  get currentBoost(): number {
    return this.boostLevel;
  }

  dispose() {
    this.stop();
    this.resizeObserver?.disconnect();
    this.composer.dispose();
    this.renderer.dispose();
  }
}
